import { differenceInCalendarDays } from 'date-fns';

/**
 * ツールバーの表示日数の選択肢。
 *
 * `buildDisplayDays`（Map/displayDays.ts）は開始日と終了日の**両端を含む**ので、
 * 終了日は「開始日 + (日数 - 1)」になる。週（7日）を +7 日にすると 8 日ぶん並ぶ。
 *
 * Why not 1 日も選べるようにする: 長距離は日をまたぐ運行を見る画面で、1 日だけ出しても
 * バーが端で切れるだけになる。日付ピッカー側も 3 日 / 7 日に絞った（ユーザー判断 2026-09-28）ので、
 * ここに 1 日を残すと同じ画面の 2 つの入口で選べる期間が食い違う。
 */
export const LONG_HAUL_SPAN_OPTIONS = [
  { label: '3日', days: 3 },
  { label: '週（7日）', days: 7 },
] as const;

/**
 * 長距離配車を開くときの既定の表示日数（両端を含む）。
 *
 * 画面を開く導線（ダッシュボードのポップアップ・ヘッダの配車計画メニュー）と、
 * path param が無いときの初期リダイレクト（LongHaul/index.tsx）で同じ値を使う。
 */
export const LONG_HAUL_DEFAULT_SPAN_DAYS = 7;

/**
 * 'YYYY-MM-DD' を Date（ローカル 0 時）にする。読めなければ null。
 *
 * Why not new Date(str): 'YYYY-MM-DD' は UTC 解釈されるので、西側のタイムゾーンでは
 * 前日になる。displayDays.ts の parseDay と同じく数値へ分解する。
 */
const parseDay = (day: string): Date | null => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!matched) return null;
  const date = new Date(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Date → 'YYYY-MM-DD'（ローカル）。ISO 変換だと UTC へずれて前日になる */
const toDayString = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * 開始日から `days` 日ぶん（両端を含む）表示するときの終了日。
 * 読めない開始日はそのまま返す（URL 直打ちで来ても画面を落とさない）。
 */
export function endOnForSpan(startOn: string, days: number): string {
  const date = parseDay(startOn);
  if (!date) return startOn;
  date.setDate(date.getDate() + days - 1);
  return toDayString(date);
}

/**
 * いま表示している日数（両端を含む）。読めない・逆転しているときは null。
 *
 * Why not ミリ秒の差で割る: 夏時間のある地域では 1 日が 24 時間にならず、日数が
 * 1 ずれる。暦日の差を見る `differenceInCalendarDays` を使う。
 */
export function spanDaysOf(startOn: string, endOn: string): number | null {
  const start = parseDay(startOn);
  const end = parseDay(endOn);
  if (!start || !end) return null;
  const diff = differenceInCalendarDays(end, start);
  return diff < 0 ? null : diff + 1;
}

/**
 * 日付ピッカーで押せない日か。終了日を `LONG_HAUL_SPAN_OPTIONS` の日数だけに絞る。
 *
 * MUI の `shouldDisableDate(day, position)` にそのまま渡せる形にしてある
 * （`@mui/x-date-pickers-pro` 8.29 の `internals/models/dateRange.d.ts:15`）。
 *
 * Why not 選んだあとに近い日数へ吸着させる: 押せてしまうと、意図した期間と違う結果に
 * なったように見える。カレンダーの時点で押せない日を灰色にして、選ぶ前に分かるようにする。
 *
 * @param start 選択中の開始日。まだ決まっていなければ null（絞りようがないので塞がない）
 */
export function isDisabledRangeDate(
  day: Date,
  position: 'start' | 'end',
  start: Date | null,
): boolean {
  if (position === 'start' || !start) return false;

  const span = differenceInCalendarDays(day, start) + 1;
  return !LONG_HAUL_SPAN_OPTIONS.some((option) => option.days === span);
}
